import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppEnvStateService } from '@services/app-env-state.service';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ConfigurationService } from 'mbs-ui-shared@services';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { getSentryEnvironment } from '../utils/get-sentry-environment';

@Injectable()
export class AppInitService {
  constructor(
    private auth: AuthFacade,
    private envStateService: AppEnvStateService,
    private environment: EnvironmentService,
    private location: Location,
    private config: ConfigurationService
  ) {}

  private get isUnauthorizedRoute() {
    const unauthorizedRoute = ['AS/Auth2FA'];

    return unauthorizedRoute.some((route) => this.location.path().includes(route));
  }

  init(): Observable<boolean> {
    const initSentry$ = (appEnvStateVersion: string) => {
      const isSentryAvailableConfigValue = this.config.get('isSentryAvailable');
      const uiVersion = this.config.get('uiversion') || appEnvStateVersion || 'unspecified';

      const isSentryAvailable = !(isSentryAvailableConfigValue === false || isSentryAvailableConfigValue === 'false');

      if (!this.environment.production || !isSentryAvailable) return of(false);

      Sentry.init({
        dsn: this.environment.sentry.dsn,
        release: uiVersion,
        integrations: [
          Sentry.breadcrumbsIntegration({
            console: false,
            dom: true,
            fetch: false,
            history: false,
            xhr: false
          }),
          Sentry.browserTracingIntegration({
            instrumentNavigation: true,
            instrumentPageLoad: true
          })
        ],
        beforeBreadcrumb: (breadcrumb, hint?) => {
          if (breadcrumb.category === 'ui.click') {
            const innerText = hint?.event?.target?.innerText;

            if (!innerText || hint?.event?.target?.children?.length) return breadcrumb;

            breadcrumb.data = { target: innerText };
          }

          return breadcrumb;
        },
        tracesSampleRate: 1,
        environment: getSentryEnvironment()
      });
      Sentry.getGlobalScope().setTag('environment', getSentryEnvironment());

      if (!this.auth.authToken && this.environment.useSessionToken && !this.isUnauthorizedRoute) {
        return this.auth.authTokenBySession$.pipe(
          catchError(() => of(false)),
          map(() => true)
        );
      }

      return of(true);
    };

    return this.envStateService.appEnvState$.pipe(
      take(1),
      map((res) => res.version),
      catchError(() => of(null)),
      switchMap((appEnvStateVersion) => initSentry$(appEnvStateVersion))
    );
  }
}
